import { useCallback, useMemo, useState } from 'react'
import { Edit } from '@mui/icons-material'
import {
  CircularProgress,
  ListItemIcon,
  MenuItem,
  Typography,
} from '@mui/material'
import { useGetIntegrationLiveListTypes } from '@/service-library/hooks/integration-live-list-types'
import { DocumentRow } from '@/types/documents'
import { ProjectGridField } from '@/types/fields'
import { ProjectGrid } from '@/types/projects'
import { getBaseGrid, getFieldsFromProject } from '@/utils/field'
import {
  createOrganizationMap,
  getDataProviderAncestor,
} from '@/utils/organization'
import { useOrganizationsContext } from '@/components/organizations/OrganizationsProvider'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import { useBulkEditValueContext } from './BulkEditValueProvider'
import useBulkActionCounts from './useBulkActionCounts'

type BulkEditValueMenuItemProps = {
  closeMenu: () => void
  field: ProjectGridField
} & ReturnType<typeof useBulkActionCounts>

export default function BulkEditValueMenuItem({
  closeMenu,
  field,
  matchingRows,
  displayCount,
  numberOfSelectedDocs,
  selectedRows,
}: BulkEditValueMenuItemProps) {
  const {
    openBulkEdit,
    pendingFieldId,
    selectedAllDocsCount,
    fetchAllMatchingRows,
  } = useBulkEditValueContext()
  const { organizations } = useOrganizationsContext()
  const { project } = useProjectContext()

  const baseGrid = getBaseGrid(project.project_grids as ProjectGrid[])

  const fieldTypeCode = field.project_grid_field_type.code
  const isLiveListPicker = fieldTypeCode === 'live-list-picker'
  const isPicker = fieldTypeCode === 'picker'

  const organizationMap = useMemo(
    () => createOrganizationMap(organizations),
    [organizations],
  )

  const { integrationLiveListTypes } = useGetIntegrationLiveListTypes({
    filters: {
      org_credential_type_id: project?.org_credential_type_id || '',
      data_format: 'live-list-entity',
      info_type_hints__id: field.info_type_id || '',
      fields__include: 'dependent_info_types_ids',
    },
    enabled: isLiveListPicker,
  })

  /**
   * Validate that the given rows satisfy org / data-provider / dependent-field
   * constraints required by picker and live-list-picker field types.
   * Returns an error message, or empty string if valid.
   */
  const validateRowEligibility = useCallback(
    (
      rows: Required<DocumentRow>[],
      gridMatchingRows: Required<DocumentRow>[],
    ): string => {
      const uniqueOrgIds = new Set(
        rows
          .map((row) => row.document?.owner_org_id)
          .filter(Boolean) as string[],
      )

      if (isLiveListPicker) {
        const dataProviderIds = new Set<string>()
        let hasOrgWithoutDataProvider = false
        for (const orgId of uniqueOrgIds) {
          const dataProviderAncestor = getDataProviderAncestor(
            orgId,
            organizationMap,
          )
          if (!dataProviderAncestor) {
            hasOrgWithoutDataProvider = true
          } else {
            dataProviderIds.add(dataProviderAncestor.id)
          }
        }

        if (hasOrgWithoutDataProvider) {
          return 'Cannot edit because a data-providing organization could not be found for the selected documents.'
        }

        if (dataProviderIds.size > 1) {
          return 'Cannot edit the value because the selected documents belong to more than one data-providing organization.'
        }

        // Check that dependent field values are consistent across selected rows
        if (integrationLiveListTypes.length > 0) {
          const dependentInfoTypeIds = new Set(
            integrationLiveListTypes.flatMap(
              (type) => type.dependent_info_types_ids,
            ),
          )

          if (dependentInfoTypeIds.size > 0) {
            const allFields = getFieldsFromProject(project)
            const dependentFields = allFields.filter((f) =>
              dependentInfoTypeIds.has(f.info_type_id || ''),
            )

            // Build a map of document_id → base row for looking up document-level dependent fields
            const baseRowsByDocId: Record<string, Required<DocumentRow>> = {}
            for (const row of rows) {
              if (row.project_grid_id === baseGrid?.id) {
                baseRowsByDocId[row.document_id] = row
              }
            }

            for (const depField of dependentFields) {
              const isDepOnSameGrid =
                depField.project_grid_id === field.project_grid_id
              const isDepOnBaseGrid = depField.project_grid_id === baseGrid?.id

              const depValues = new Set<string>()

              for (const row of gridMatchingRows) {
                let valueRow: Required<DocumentRow> | undefined

                if (isDepOnSameGrid) {
                  valueRow = row
                } else if (isDepOnBaseGrid) {
                  valueRow = baseRowsByDocId[row.document_id]
                }

                if (valueRow) {
                  const depValue = valueRow.document_row_values?.find(
                    (v) => v.project_grid_field_id === depField.id,
                  )
                  depValues.add(depValue?.final_value || '')
                }
              }

              // All dependent values are empty
              if (depValues.size === 1 && depValues.has('')) {
                return `Cannot edit because ${depField.name} has not been set on the selected rows.`
              }

              // Dependent values differ across selected rows
              if (depValues.size > 1) {
                return `Cannot edit because the selected rows have different ${depField.name} values.`
              }
            }
          }
        }
      }

      if (isPicker) {
        if (uniqueOrgIds.size > 1) {
          return 'Cannot edit the value because the selected documents belong to more than one organization.'
        }
      }

      return ''
    },
    [
      isLiveListPicker,
      isPicker,
      integrationLiveListTypes,
      organizationMap,
      project,
      baseGrid?.id,
      field.project_grid_id,
    ],
  )

  const { isDisabled, disabledReason } = useMemo(() => {
    if (numberOfSelectedDocs === 0) {
      return { isDisabled: true, disabledReason: '' }
    }

    if (field.input_behavior === 'manual_not_allowed') {
      return {
        isDisabled: true,
        disabledReason: 'Manual editing is not allowed for this field.',
      }
    }

    if (pendingFieldId) {
      return {
        isDisabled: true,
        disabledReason: 'Cannot edit while another field is being updated.',
      }
    }

    const reason = validateRowEligibility(selectedRows, matchingRows)
    if (reason) {
      return { isDisabled: true, disabledReason: reason }
    }

    return { isDisabled: false, disabledReason: '' }
  }, [
    numberOfSelectedDocs,
    field.input_behavior,
    pendingFieldId,
    selectedRows,
    matchingRows,
    validateRowEligibility,
  ])

  const [isLoadingAll, setIsLoadingAll] = useState(false)
  const [selectAllError, setSelectAllError] = useState('')

  async function handleClick() {
    if (selectedAllDocsCount) {
      setIsLoadingAll(true)
      const { allRows, allMatchingRows } = await fetchAllMatchingRows(field)
      setIsLoadingAll(false)

      // Re-validate against the full set of rows since the initial
      // validation only ran against the loaded subset.
      const reason = validateRowEligibility(allRows, allMatchingRows)
      if (reason) {
        setSelectAllError(reason)
        return
      }

      closeMenu()
      openBulkEdit(field, allMatchingRows)
    } else {
      closeMenu()
      openBulkEdit(field, matchingRows)
    }
  }

  return (
    <>
      {(disabledReason || selectAllError) && (
        <Typography
          variant="caption"
          color="warning.main"
          sx={{ px: 2, pb: 1, display: 'block' }}
        >
          {disabledReason || selectAllError}
        </Typography>
      )}
      <MenuItem
        disabled={
          isDisabled || displayCount === 0 || isLoadingAll || !!selectAllError
        }
        onClick={handleClick}
      >
        <ListItemIcon>
          {isLoadingAll ? (
            <CircularProgress size={20} />
          ) : (
            <Edit fontSize="small" />
          )}
        </ListItemIcon>
        {`Edit ${field.name}`}
      </MenuItem>
    </>
  )
}
